// Global money-input hygiene.
//
// Every amount / price / rate / discount input in the app is a plain
// <input type="number" step="0.01"> or an <input type="text" inputmode="decimal">.
// Owner rule (2026-09-17): "we shouldn't have commas in the pricing inputs" — and
// by extension no commas in any money input, anywhere in the app. Rather than
// touch every one of the 34 numeric inputs across the tenant + admin surface, we
// install one delegated listener at document level and strip commas as they are
// typed or pasted. Right-alignment for the same set of inputs is CSS-only
// (aboo.css `input[type="number"], input[inputmode="decimal"] { text-align: right }`).
//
// A field that legitimately wants commas (a comma-separated list of ids, an
// export filename) can opt out with `data-allow-commas` on the element.

const MONEY_SELECTOR =
  'input[type="number"], input[inputmode="decimal"], input[inputmode="numeric"].money-in';

function isMoneyInput(el){
  return el && el.matches && el.matches(MONEY_SELECTOR) && !el.hasAttribute('data-allow-commas');
}

// Turn a locale-formatted money string into the app's canonical dot-decimal form.
// Handles US "1,200.00" (thousands comma) AND South-African / European "1 200,00"
// (decimal comma) as one rule: if the value ends with ",X" or ",XX" that is the
// decimal separator; any earlier comma is a thousands separator and drops. Spaces
// also drop. If no trailing decimal comma is present, every comma is a thousands
// separator and drops. The result contains only digits and (at most one) dot.
export function normalizeMoneyValue(v){
  let s = String(v == null ? '' : v).replace(/\s+/g, '');
  const decimalComma = /^(.*),(\d{1,2})$/.exec(s);
  if(decimalComma){
    s = decimalComma[1].replace(/,/g, '') + '.' + decimalComma[2];
  } else {
    s = s.replace(/,/g, '');
  }
  return s;
}

// A comma with nothing, or one or two digits, after it may still be a DECIMAL comma on its way ("12," → "12,50":
// Finland, Germany, the Netherlands — and South Africa's "1 200,00" — write money that way). Stripping it the moment
// it was typed turned "12,50" into 1250. So while typing it is left alone; it resolves as soon as it can: a third
// digit makes it a thousands comma (stripped, as before — "1,000"), and leaving the field or Enter reads ",X" / ",XX"
// as the decimal point (owner 2026-10-19: a euro amount typed must read as typed).
const PENDING_DECIMAL_COMMA = /,\d{0,2}$/;

export function isPendingDecimalComma(v){
  return PENDING_DECIMAL_COMMA.test(String(v == null ? '' : v).replace(/\s+/g, ''));
}

function stripCommas(el, final){
  const before = el.value;
  if(!final && isPendingDecimalComma(before)) return;
  const after = normalizeMoneyValue(before);
  if(before !== after){
    // Preserve caret position across the strip.
    const drop = before.length - after.length;
    const pos = (el.selectionStart != null ? el.selectionStart : after.length) - Math.max(0, drop);
    el.value = after;
    try { el.setSelectionRange(pos, pos); } catch(_){}
    // Fire a synthetic input event so signal/state observers re-read the value.
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

let installed = false;

export function installMoneyInputHygiene(){
  if(installed || typeof document === 'undefined') return;
  installed = true;

  // Live typing: strip a comma as it arrives — unless it may still be a decimal comma (see above).
  document.addEventListener('input', e => {
    if(isMoneyInput(e.target)) stripCommas(e.target);
  }, true);   // capture so we run BEFORE any per-input handler

  // Leaving the field, or Enter (a submit), settles a pending decimal comma: "12,50" → "12.50" before any
  // per-input handler reads the value.
  document.addEventListener('focusout', e => {
    if(isMoneyInput(e.target)) stripCommas(e.target, true);
  }, true);
  document.addEventListener('keydown', e => {
    if(e.key === 'Enter' && isMoneyInput(e.target)) stripCommas(e.target, true);
  }, true);

  // Paste: normalise the clipboard text before it lands so a locale-formatted
  // "R 1 200,00" or "$1,200.00" pastes correctly as "1200.00".
  document.addEventListener('paste', e => {
    if(!isMoneyInput(e.target)) return;
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if(!text) return;
    const clean = normalizeMoneyValue(text.replace(/[^\d.,\s-]/g, ''));   // drop currency symbols
    if(clean !== text){
      e.preventDefault();
      const el = e.target;
      const start = el.selectionStart || 0, end = el.selectionEnd || 0;
      el.value = el.value.slice(0, start) + clean + el.value.slice(end);
      const pos = start + clean.length;
      try { el.setSelectionRange(pos, pos); } catch(_){}
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, true);
}
